import { uploadImage, getUploadedMediaRecord } from "../http/uploads";
import { showToast } from "../store/slices/common";
import { validateFileExtension } from "./index";

const VALID_FORMATS = ["image/jpeg", "image/jpg", "image/png", "image/jfif"];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

/**
 * Keep a ref aligned with sticker state. Call during render. Uploads must
 * update `ref.current` themselves so the next file in the same drop sees it
 * before React re-renders.
 */
export function syncStickerListRef(listRef, list) {
  if (listRef.rendered !== list) {
    listRef.current = list;
    listRef.rendered = list;
  }
}

/** Append one uploaded sticker. Updates `listRef.current` synchronously. */
export function rememberUploadedSticker(listRef, media, max) {
  const mediaId = media?._id ? String(media._id) : "";
  if (!/^[0-9a-fA-F]{24}$/.test(mediaId) || !media?.url) return listRef.current;
  const prev = listRef.current || [];
  if (prev.some((sticker) => String(sticker.mediaId) === mediaId)) return prev;
  if (prev.length >= max) return prev;
  const next = [...prev, { mediaId, url: media.url }];
  listRef.current = next;
  return next;
}

/**
 * Ant Design calls `customRequest` once per file, including a multi-file drop.
 * Slots are reserved before the upload starts so concurrent files cannot pass
 * `max`. The whole drop is reported in one toast.
 *
 * `getCurrent` must count stickers already stored. `onUploaded` must update
 * that count synchronously (see `rememberUploadedSticker`) before returning.
 */
export function createStickerUploadHandler({
  max,
  getCurrent,
  folder,
  metadata,
  onUploaded,
  dispatch,
  setLoading,
  limitMessage,
}) {
  let reserved = 0;
  let inFlight = 0;
  const batch = {
    uploaded: 0,
    failed: 0,
    invalid: 0,
    oversized: 0,
    overflow: 0,
    lastError: "",
  };
  let flushTimer = null;

  const emitSummary = () => {
    const { uploaded, failed, invalid, oversized, overflow, lastError } = batch;
    if (!uploaded && !failed && !invalid && !oversized && !overflow) return;
    batch.uploaded = 0;
    batch.failed = 0;
    batch.invalid = 0;
    batch.oversized = 0;
    batch.overflow = 0;
    batch.lastError = "";

    const parts = [];
    if (uploaded === 1) parts.push("1 sticker uploaded.");
    else if (uploaded > 1) parts.push(`${uploaded} stickers uploaded.`);
    if (failed === 1) {
      parts.push(lastError || "1 sticker failed to upload.");
    } else if (failed > 1) {
      parts.push(`${failed} stickers failed to upload.`);
    }
    if (invalid) parts.push("Use PNG or JPG images.");
    if (oversized) parts.push("Each sticker must be 5MB or smaller.");
    if (overflow) {
      parts.push(limitMessage || `You can attach up to ${max} stickers.`);
    }

    const problem = failed > 0 || invalid > 0 || oversized > 0 || overflow > 0;
    dispatch(
      showToast({
        color: !problem ? "success" : uploaded > 0 ? "warning" : "error",
        msg: parts.join(" "),
      })
    );
  };

  const scheduleSummary = () => {
    if (inFlight > 0) return;
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(() => {
      flushTimer = null;
      if (inFlight > 0) return;
      emitSummary();
    }, 0);
  };

  return async (options) => {
    const { file, onSuccess, onError } = options;

    if (getCurrent() + reserved >= max) {
      batch.overflow += 1;
      onError(new Error("Sticker limit reached"));
      scheduleSummary();
      return;
    }

    const isValidMimeType = file.type && VALID_FORMATS.includes(file.type);
    const isValidExtension = validateFileExtension(file.name || "", "");
    if (!isValidMimeType && !isValidExtension) {
      batch.invalid += 1;
      onError(new Error("Invalid file format"));
      scheduleSummary();
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      batch.oversized += 1;
      onError(new Error("File size too large"));
      scheduleSummary();
      return;
    }

    reserved += 1;
    inFlight += 1;
    if (inFlight === 1 && setLoading) setLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", folder);
      const meta = typeof metadata === "function" ? metadata() : metadata;
      if (meta) formData.append("metadata", JSON.stringify(meta));
      const res = await uploadImage(formData);
      const media = getUploadedMediaRecord(res);
      if (!media?._id || !media?.url) {
        throw new Error("Upload response did not include a media record");
      }
      onUploaded(media);
      batch.uploaded += 1;
      onSuccess(media);
    } catch (error) {
      console.error(error);
      batch.failed += 1;
      batch.lastError =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to upload sticker.";
      onError(error);
    } finally {
      reserved -= 1;
      inFlight -= 1;
      if (inFlight === 0 && setLoading) setLoading(false);
      scheduleSummary();
    }
  };
}
